import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import ResultsSection from './ResultsSection.jsx';

const normalizeDayValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized !== '' ? normalized : null;
};

const parsePositiveDay = (value) => {
  const normalized = normalizeDayValue(value);
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
};

export default function ResultsWidget({
  discipline = 'rugby',
  competitionIds = [],
  competitionInfos = [],
  day = null,
  onNavigate,
  onDayChange,
  widgetConfigurationId=null,
  title,
}) {
  const [sectionMeta, setSectionMeta] = useState({
    selectedDay: null,
    nbDays: null,
  });

  const isMultiCompetition = competitionIds.length > 1;
  const competitionMeta = competitionInfos?.[0] || {};
  const resolvedSeason = competitionMeta?.season || null;
  const rounds = Array.isArray(competitionMeta?.rounds) ? competitionMeta.rounds : [];

  const roundChoices = useMemo(() => {
    const seen = new Set();

    return rounds.filter((round) => {
      const key = String(round?.key || round?.label || '').trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rounds]);

  const hasMultipleRoundChoices = roundChoices.length > 1;

  const displayedDay = useMemo(() => {
    return normalizeDayValue(sectionMeta.selectedDay) ?? normalizeDayValue(day);
  }, [sectionMeta.selectedDay, day]);

  const displayedNumericDay = useMemo(() => {
    return parsePositiveDay(displayedDay);
  }, [displayedDay]);

  const maxDay = useMemo(() => {
    return (
      parsePositiveDay(sectionMeta.nbDays) ??
      parsePositiveDay(competitionMeta?.nbDays)
    );
  }, [sectionMeta.nbDays, competitionMeta]);

  const currentRound = useMemo(() => {
    if (!displayedDay) return null;

    return (
      rounds.find(
        (round) =>
          Array.isArray(round?.days) &&
          round.days.map(normalizeDayValue).includes(displayedDay)
      ) || null
    );
  }, [rounds, displayedDay]);

  const selectedRoundKey = useMemo(() => {
    if (currentRound?.key) return currentRound.key;
    if (displayedNumericDay) return 'regular';
    return '';
  }, [currentRound, displayedNumericDay]);

  const isNumericRound =
    currentRound?.type === 'numeric' ||
    (!currentRound && displayedNumericDay != null);

  const canGoPrevious = Boolean(displayedNumericDay && displayedNumericDay > 1);
  const canGoNext = Boolean(
    displayedNumericDay && maxDay && displayedNumericDay < maxDay
  );
  const canShowDayNavigation =
    !isMultiCompetition && Boolean(displayedDay) && isNumericRound;

  const handlePreviousDay = () => {
    if (!canGoPrevious || !onDayChange || displayedNumericDay == null) return;
    onDayChange(String(displayedNumericDay - 1));
  };

  const handleNextDay = () => {
    if (!canGoNext || !onDayChange || displayedNumericDay == null) return;
    onDayChange(String(displayedNumericDay + 1));
  };

  const handleRoundChange = (event) => {
    const nextRoundKey = event.target.value;
    const nextRound = roundChoices.find((round) => round.key === nextRoundKey);

    if (!nextRound || !onDayChange) return;

    if (nextRound.type === 'numeric') {
      const currentDayInRound =
        displayedDay &&
        Array.isArray(nextRound.days) &&
        nextRound.days.map(normalizeDayValue).includes(displayedDay)
          ? displayedDay
          : null;

      const fallbackNumericDay =
        currentDayInRound ||
        normalizeDayValue(competitionMeta?.defaultDay) ||
        normalizeDayValue(nextRound.days?.[0]) ||
        '1';

      onDayChange(fallbackNumericDay);
      return;
    }

    const firstLiteralDay = normalizeDayValue(nextRound.days?.[0]);
    if (firstLiteralDay) {
      onDayChange(firstLiteralDay);
    }
  };

  const handleMetaChange = useCallback((meta) => {
    setSectionMeta((prev) => {
      const nextSelectedDay = normalizeDayValue(meta?.selectedDay);
      const nextNbDays = parsePositiveDay(meta?.nbDays);

      if (
        prev.selectedDay === nextSelectedDay &&
        prev.nbDays === nextNbDays
      ) {
        return prev;
      }

      return {
        selectedDay: nextSelectedDay,
        nbDays: nextNbDays,
      };
    });
  }, []);

  if (!competitionIds.length) return null;

  return (
    <Box
      sx={{
        width: '100%',
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 2, md: 3 },
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
        <Stack spacing={2}>
          {title ? (
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1f1f1f' }}>
              {title}
            </Typography>
          ) : null}

          {competitionInfos.length > 0 ? (
            <Stack spacing={0.5}>
              {competitionInfos.map((competition) => (
                <Box key={competition.id || competition.name}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 700, color: '#374151' }}
                  >
                    {competition.name}
                  </Typography>
                  {competition.season ? (
                    <Typography variant="body2" color="text.secondary">
                      Saison {competition.season}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : null}

          {isMultiCompetition ? (
            <Typography variant="body2" color="text.secondary">
              Résultats cumulés - toutes journées confondues
            </Typography>
          ) : (
            <>
              {hasMultipleRoundChoices ? (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 240 }}>
                    <Select
                      value={selectedRoundKey}
                      onChange={handleRoundChange}
                      displayEmpty
                    >
                      {roundChoices.map((round) => (
                        <MenuItem key={round.key} value={round.key}>
                          {round.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : null}

              {canShowDayNavigation ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    borderTop: '1px solid #d9d9d9',
                    borderBottom: '1px solid #d9d9d9',
                    py: 0.75,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handlePreviousDay}
                    disabled={!canGoPrevious}
                    aria-label="Journée précédente"
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 140, textAlign: 'center', fontWeight: 700 }}
                  >
                    Journée {displayedNumericDay}
                  </Typography>

                  <IconButton
                    size="small"
                    onClick={handleNextDay}
                    disabled={!canGoNext}
                    aria-label="Journée suivante"
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : !isMultiCompetition && hasMultipleRoundChoices && currentRound?.label ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderTop: '1px solid #d9d9d9',
                    borderBottom: '1px solid #d9d9d9',
                    py: 0.75,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 140, textAlign: 'center', fontWeight: 700 }}
                  >
                    {currentRound.label}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}

          <ResultsSection
            discipline={discipline}
            season={resolvedSeason}
            competitionIds={competitionIds}
            day={day}
            widgetConfigurationId={widgetConfigurationId}
            onNavigate={onNavigate}
            onMetaChange={handleMetaChange}
          />
        </Stack>
      </Box>
    </Box>
  );
}